import { BaseView, Container, dispatcher, ingenuity, Linear, Meter, SpineBase, Sprite } from "@quest/base-core";
import { eventDispatchers } from "../Constants/EventDispatchers";
import { GameParserModel } from "../Platform/GameParserModel";
import { gameConstants } from "../Constants/gameConstants";
import { ITween } from "@quest/adapter-interface";

export class QLinePlayerMsgController {
  // UI and animation-related elements
  protected view: BaseView;
  protected qLinesPlayerMessageContainer: Container;
  protected playerMsgBaseSpine: SpineBase;
  protected playerMsgSymbolCount: Meter;
  protected playerMsgSymbolContainer: Container;
  protected playerMsgContainer: Container;
  protected playerMsgWinMeter: Meter;
  protected playerMsgPaysText: Sprite;
  protected symbolId: number;
  protected symbolCount: number;
  protected winAmount: number;
  private hidePayerMsgTween: ITween;
  private showPlayerMsgTween: ITween;
  protected playerMsgSymbol: string = gameConstants.PLAYER_MESSAGE_SYMBOL_STRING;

  constructor(view: any) {
    this.view = view;
    this.init();
  }

  // Initializes the controller by setting up elements and event listeners
  protected init(): void {
    this.initializeElements();
    this.subscribeEvents();
  }

  // Retrieves and stores references to visual/UI components from the view
  protected initializeElements(): void {
    this.qLinesPlayerMessageContainer = this.view.getContainerByID("qLinesPlayerMessageContainer");
    this.playerMsgBaseSpine = this.view.getSpineById("playerMessageBase");
    this.playerMsgSymbolCount = this.view.getMeterById("playerMsgSymbolCount") as Meter;
    this.playerMsgSymbolContainer = this.view.getContainerByID("playerMsgSymbolContainer");
    this.playerMsgContainer = this.view.getContainerByID("playerMsgContainer");
    this.playerMsgWinMeter = this.view.getMeterById("playerMsgWinMeter") as Meter;
  }

  // Subscribes to the dispatcher events to show/hide the player message
  protected subscribeEvents() {
    dispatcher.on(eventDispatchers.SHOW_QLINE_PLAYER_MSG, this.onShowQlinePlayerMsg, this);
    dispatcher.on(eventDispatchers.HIDE_QLINE_PLAYER_MSG, this.onHideQlinePlayerMsg, this);
  }

  // Event handler to show the player message when the relevant event is triggered
  protected onShowQlinePlayerMsg(): void {
    ingenuity.currentGame.tweens.remove(this.hidePayerMsgTween);
    ingenuity.currentGame.tweens.remove(this.showPlayerMsgTween);
    this.hidePlayerMsg();

    this.setPlayerMsgValues();
    this.addContainerToTop();
    this.showPlayerMsgBase();
    this.setPlayerMsg();
    this.showPlayerMsg();
  }

  // Event handler to hide the player message when the relevant event is triggered
  protected onHideQlinePlayerMsg(): void {
    ingenuity.currentGame.tweens.remove(this.showPlayerMsgTween);
    ingenuity.currentGame.tweens.remove(this.hidePayerMsgTween);
    this.hidePlayerMsgBase();
  }

  // Retrieves and sets the values to be displayed in the message (symbol count, win amount, etc.)
  protected setPlayerMsgValues(): void {
    const winData = (ingenuity.parserModel as GameParserModel).getWinLinesData()[
      (ingenuity.parserModel as GameParserModel).getWinLinesData().length -
      (ingenuity.parserModel as GameParserModel).getCascadeLineToggleCount()
    ];
    this.symbolCount = winData.winSymLen;
    this.winAmount = winData.win;

    // Map certain special 'no' values to fixed symbol IDs
    if (winData.no === 200) {
      this.symbolId = 8;
    } else if (winData.no === 300) {
      this.symbolId = 9;
    } else {
      this.symbolId = winData.no;
    }
  }

  // Ensures the message container is on top of the display hierarchy
  protected addContainerToTop(): void {
    this.view.addChild(this.qLinesPlayerMessageContainer);
  }

  // Displays the base animation and loops it after the intro
  protected showPlayerMsgBase(): void {
    this.qLinesPlayerMessageContainer.visible = true;
    this.playerMsgBaseSpine.visible = true;
    this.playerMsgBaseSpine.playAnim("intro");
    this.playerMsgBaseSpine.onComplete.dispose();
    this.playerMsgBaseSpine.onComplete.addOnce(() => {
      this.playerMsgBaseSpine.playAnim("loop", true);
    });
  }

  // Sets win amount and symbol count in the respective UI components
  protected setPlayerMsg(): void {
    this.playerMsgWinMeter.setCurrencyFormattedValue(this.winAmount.toString());
    this.playerMsgSymbolCount.setValue(this.symbolCount);
  }

  // Makes relevant UI elements visible and animates the message fade-in
  protected showPlayerMsg(): void {
    this.playerMsgSymbolCount.visible = true;
    this.playerMsgSymbolContainer.visible = true;
    this.playerMsgSymbolContainer.children[this.symbolId].visible = true;
    this.playerMsgContainer.getChildByName("playerMsgPaysText").visible = true;
    this.playerMsgWinMeter.visible = true;
    this.setAlignment();
    this.qLinesPlayerMessageContainer.alpha = 0;
    this.showPlayerMsgTween = ingenuity.currentGame.add
      .tween(this.qLinesPlayerMessageContainer)
      .to({ alpha: 1 }, 200, Linear.None, true, 0, 0, false);
    this.showPlayerMsgTween.onComplete.addOnce(() => {
      this.showPlayerMsgTween.stop();
      ingenuity.currentGame.tweens.remove(this.showPlayerMsgTween);
    }, this);
  }

  // Aligns the container and meter visuals horizontally centered
  protected setAlignment(): void {
    this.playerMsgContainer.setPivot(this.playerMsgContainer.width / 2, 0);
    this.playerMsgSymbolCount.setPivot(this.playerMsgSymbolCount.width / 2, 0);
  }

  // Fades out the message and then hides the UI elements after the animation
  protected hidePlayerMsgBase(): void {
    this.hidePayerMsgTween = ingenuity.currentGame.add
      .tween(this.qLinesPlayerMessageContainer)
      .to({ alpha: 0 }, 200, Linear.None, true, 0, 0, false);
    this.hidePayerMsgTween.onComplete.addOnce(() => {
      this.hidePayerMsgTween.stop();
      ingenuity.currentGame.tweens.remove(this.hidePayerMsgTween);
      this.hidePlayerMsg();
    }, this);
  }

  // Hides all message-related UI elements and resets visibility
  protected hidePlayerMsg(): void {
    this.playerMsgBaseSpine.visible = false;
    this.qLinesPlayerMessageContainer.visible = false;
      this.playerMsgSymbolCount.visible = false;
      this.playerMsgSymbolContainer.visible = false;
      this.playerMsgSymbolContainer.children[this.symbolId] &&  (this.playerMsgSymbolContainer.children[this.symbolId].visible = false);
      this.playerMsgContainer.getChildByName("playerMsgPaysText").visible = false;
      this.playerMsgWinMeter.visible = false;
  }  
}
